import React, { useCallback, useEffect, useState } from 'react';
import API from '../../api/API';
import { useMe } from '../../hooks/useMe';
import TabUtenti from './utenti/TabUtenti';
import TabGruppi from './utenti/TabGruppi';
import TabPermessi from './utenti/TabPermessi';
import TabApprovazioni from './utenti/TabApprovazioni';
import './AUtenti.css';

/**
 * Utenti, gruppi di utenti e matrice dei permessi.
 *
 * I tre tab lavorano sugli stessi dati, quindi l'elenco di utenti e gruppi sta qui e si ricarica in
 * un posto solo: il tab Permessi ha bisogno di sapere chi sono i principal, e i tab Utenti e Gruppi
 * hanno bisogno di sapere quante celle ha ognuno.
 */
const AUtenti = () => {
  const { me, ambiti, daDecidere, loading: loadingMe, ricarica: ricaricaMe } = useMe();
  const [tab, setTab] = useState('utenti');
  const [utenti, setUtenti] = useState([]);
  const [gruppi, setGruppi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);

  // il principal aperto nel tab Permessi, scelto da uno degli altri due tab
  const [selezione, setSelezione] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true);
    const [resUtenti, resGruppi] = await Promise.all([API.utenti.elenco(), API.gruppiUtenti.elenco()]);

    if (resUtenti.isSuccess) setUtenti(resUtenti.data || []);
    if (resGruppi.isSuccess) setGruppi(resGruppi.data || []);

    setErrore(resUtenti.isSuccess && resGruppi.isSuccess
      ? null
      : (resUtenti.errorMsg || resGruppi.errorMsg || 'Caricamento non riuscito'));
    setLoading(false);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  /** Dopo una scrittura: gli elenchi cambiano, e la propria matrice puo' essere cambiata anche lei. */
  const dopoScrittura = async () => {
    await carica();
    await ricaricaMe();
  };

  const apriPermessi = (tipo, id, nome) => {
    setSelezione({ tipo, id, nome });
    setTab('permessi');
  };

  if (loading || loadingMe) {
    return (
      <div className="utenti-container">
        <div className="utenti-loading">
          <div className="loading-spinner"></div>
          <p>Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'utenti', label: 'Utenti', icona: 'fa-user', conteggio: utenti.length },
    { id: 'gruppi', label: 'Gruppi', icona: 'fa-users', conteggio: gruppi.length },
    { id: 'permessi', label: 'Permessi', icona: 'fa-table', conteggio: null },
    { id: 'approvazioni', label: 'Approvazioni', icona: 'fa-inbox', conteggio: daDecidere || null }
  ];

  return (
    <div className="utenti-container">
      <div className="utenti-header">
        <div className="utenti-title">
          <i className="fas fa-user-shield"></i>
          <h1>Utenti e permessi</h1>
        </div>
        {me?.bypass && (
          <div className="utenti-badge utenti-badge-bypass" title="Appartieni a un gruppo con bypass: vedi e puoi tutto">
            <i className="fas fa-key"></i> superadmin
          </div>
        )}
      </div>

      {errore && (
        <div className="utenti-errore">
          <i className="fas fa-exclamation-triangle"></i> {errore}
        </div>
      )}

      <div className="utenti-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`utenti-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <i className={`fas ${t.icona}`}></i>
            <span>{t.label}</span>
            {t.conteggio !== null && <span className="utenti-tab-badge">{t.conteggio}</span>}
          </button>
        ))}
      </div>

      <div className="utenti-pannello">
        {tab === 'utenti' && (
          <TabUtenti
            utenti={utenti}
            gruppi={gruppi}
            me={me}
            ambiti={ambiti}
            onCambiato={dopoScrittura}
            onApriPermessi={(id, nome) => apriPermessi('U', id, nome)}
          />
        )}

        {tab === 'gruppi' && (
          <TabGruppi
            gruppi={gruppi}
            utenti={utenti}
            me={me}
            ambiti={ambiti}
            onCambiato={dopoScrittura}
            onApriPermessi={(id, nome) => apriPermessi('G', id, nome)}
          />
        )}

        {tab === 'approvazioni' && <TabApprovazioni onCambiato={dopoScrittura} />}

        {tab === 'permessi' && (
          <TabPermessi
            utenti={utenti}
            gruppi={gruppi}
            me={me}
            ambiti={ambiti}
            selezione={selezione}
            onSelezione={setSelezione}
            onCambiato={dopoScrittura}
          />
        )}
      </div>
    </div>
  );
};

export default AUtenti;
